import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fmtEUR, API } from "@/lib/api";
import { toast } from "sonner";
import { Printer, Download, Trash2, Pencil, Plus, Package, Truck, BarChart3, Boxes, ClipboardList, Upload, Ticket, X, MessageSquare, Check, Inbox } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const STATUS_OPTIONS = ["pending_kitchen", "preparing", "ready", "dispatched", "delivered", "cancelled"];
const STATUS_LABEL = {
  pending_kitchen: "Pending Kitchen", preparing: "Preparing",
  ready: "Ready for Courier", dispatched: "Dispatched", delivered: "Delivered", cancelled: "Cancelled",
};
const CATEGORIES = ["protein-bars", "granola", "sweets", "bundles"];
const PIE_COLORS = ["#8C3B1D", "#2D4030", "#D9822B", "#3D3A34", "#D5CBB0"];

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (!user.is_admin) { nav("/"); return; }
  }, [user, loading]);

  if (loading || !user?.is_admin) return <div className="text-center py-24">Loading…</div>;

  const TABS = [
    { id: "orders", label: "Orders", icon: ClipboardList },
    { id: "products", label: "Products", icon: Boxes },
    { id: "shipping", label: "Shipping", icon: Truck },
    { id: "discounts", label: "Discounts", icon: Ticket },
    { id: "reviews", label: "Reviews", icon: MessageSquare },
    { id: "reports", label: "Reports", icon: BarChart3 },
    { id: "enquiries", label: "Enquiries", icon: Inbox },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="admin-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-forest)]">Command centre</div>
        <h1 className="font-serif text-3xl lg:text-4xl">Brunchi Admin</h1>
      </div>
      <div className="flex gap-2 border-b border-[color:var(--brunchi-stone)] mb-8 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                tab === t.id ? "border-[color:var(--brunchi-terracotta)] text-[color:var(--brunchi-terracotta)]" : "border-transparent text-[color:var(--brunchi-ink-soft)]"
              }`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "orders" && <OrdersTab />}
      {tab === "products" && <ProductsTab />}
      {tab === "shipping" && <ShippingTab />}
      {tab === "discounts" && <DiscountsTab />}
      {tab === "reviews" && <ReviewsTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "enquiries" && <EnquiriesTab />}
    </div>
  );
}

function EnquiriesTab() {
  const [kind, setKind] = useState("");
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/enquiries", { params: kind ? { kind } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  const markRead = async (id) => { await api.put(`/admin/enquiries/${id}/read`); load(); };
  return (
    <div data-testid="enquiries-tab">
      <div className="flex gap-2 mb-4">
        {[["", "all"], ["contact", "contact"], ["catering", "catering"]].map(([v, l]) => (
          <button key={l} onClick={() => setKind(v)} data-testid={`enquiries-filter-${l}`}
            className={`px-4 py-2 rounded-full text-sm border ${kind === v ? "bg-[color:var(--brunchi-ink)] text-white border-[color:var(--brunchi-ink)]" : "bg-white border-[color:var(--brunchi-stone-2)]"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-sm text-[color:var(--brunchi-ink-soft)] py-16 text-center">No enquiries yet.</div>}
        {items.map((q) => (
          <div key={q.id} className={`bg-white rounded-xl border p-5 ${q.is_read ? "border-[color:var(--brunchi-stone)]" : "border-[color:var(--brunchi-terracotta)]"}`} data-testid={`enquiry-${q.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-2 text-sm">
              <span className={`px-2 py-0.5 rounded-full text-xs uppercase tracking-wider ${q.kind === "catering" ? "bg-[color:var(--brunchi-green-light)] text-[color:var(--brunchi-green-hover)]" : "bg-[color:var(--brunchi-stone)]"}`}>{q.kind}</span>
              <span className="font-display font-semibold">{q.kind === "catering" ? `${q.company} · ${q.contact_name}` : q.name}</span>
              <a href={`mailto:${q.email}`} className="underline">{q.email}</a>
              {q.phone && <span>{q.phone}</span>}
              <span className="ml-auto font-mono text-xs text-[color:var(--brunchi-ink-soft)]">{new Date(q.created_at).toLocaleString()}</span>
            </div>
            {q.kind === "catering" && (
              <div className="text-xs font-mono text-[color:var(--brunchi-ink-soft)] mb-2">{q.service} · {q.people_count} people · {q.event_date || "date TBC"}</div>
            )}
            <p className="text-sm whitespace-pre-wrap">{q.message}</p>
            {!q.is_read && (
              <button onClick={() => markRead(q.id)} data-testid={`enquiry-read-${q.id}`}
                className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[color:var(--brunchi-stone-2)] hover:bg-[color:var(--brunchi-stone)]">
                <Check size={12} /> Mark as read
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsTab() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews", { params: { status } }).then((r) => setItems(r.data));
  useEffect(load, [status]);
  const approve = async (id, ok) => {
    try { await api.put(`/admin/reviews/${id}`, { approve: ok }); toast.success(ok ? "Approved" : "Unapproved"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this review?")) return; await api.delete(`/admin/reviews/${id}`); load(); };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            data-testid={`reviews-filter-${s}`}
            className={`px-4 py-2 rounded-full text-sm border ${
              status === s
                ? "bg-[color:var(--brunchi-ink)] text-white border-[color:var(--brunchi-ink)]"
                : "bg-white border-[color:var(--brunchi-stone-2)]"
            }`}>{s}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-sm text-[color:var(--brunchi-ink-soft)] py-16 text-center">No {status} reviews.</div>}
        {items.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="text-[color:var(--brunchi-amber)] text-sm font-mono">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
              <div className="font-display font-semibold">{r.user_name}</div>
              <div className="text-xs text-[color:var(--brunchi-ink-soft)]">on <span className="font-mono">{r.product_slug}</span></div>
              <div className="text-xs text-[color:var(--brunchi-ink-soft)] ml-auto">{r.created_at?.slice(0, 10)}</div>
            </div>
            {r.title && <div className="font-display text-sm font-medium mb-1">{r.title}</div>}
            <div className="text-sm text-[color:var(--brunchi-ink-soft)] mb-3">{r.comment}</div>
            <div className="flex gap-2">
              {status === "pending" && (
                <button onClick={() => approve(r.id, true)} data-testid={`review-approve-${r.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-primary text-xs">
                  <Check size={12} /> Approve & publish
                </button>
              )}
              {status === "approved" && (
                <button onClick={() => approve(r.id, false)} data-testid={`review-unapprove-${r.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-outline text-xs">
                  Unpublish
                </button>
              )}
              <button onClick={() => del(r.id)} data-testid={`review-delete-${r.id}`}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[color:var(--brunchi-error)] hover:bg-red-50">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("paid_all");

  const load = () => {
    api.get("/admin/orders", { params: { status: filter } }).then((r) => setOrders(r.data));
  };
  useEffect(load, [filter]);

  const changeStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); load(); toast.success("Updated"); }
    catch { toast.error("Update failed"); }
  };

  const printLabel = async (id, num) => {
    try {
      const res = await api.get(`/admin/orders/${id}/label`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (w) setTimeout(() => w.print(), 500);
    } catch { toast.error("Label failed"); }
  };

  const printQueue = async () => {
    try {
      const res = await api.get(`/admin/labels/queue`, {
        params: { status: filter === "paid_all" ? "all" : filter },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (w) setTimeout(() => w.print(), 500);
      toast.success("Label queue ready for Zebra ZD421");
    } catch (err) {
      toast.error(err.response?.status === 404 ? "No orders in queue" : "Queue failed");
    }
  };

  const downloadCsv = () => {
    const token = localStorage.getItem("brunchi_token");
    fetch(`${API}/admin/reports/orders.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "brunchi-orders.csv"; a.click();
      });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="admin-orders-filter"
          className="px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm">
          <option value="paid_all">All paid orders</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <button onClick={printQueue} data-testid="admin-print-queue-btn"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full pill-primary text-sm font-medium">
          <Printer size={14} /> Print label queue
        </button>
        <button onClick={downloadCsv} data-testid="admin-csv-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill-outline text-sm font-medium">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead className="bg-[color:var(--brunchi-cream-2)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Order</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">City / Country</th>
              <th className="px-4 py-3 font-medium">Items</th>
              <th className="px-4 py-3 font-medium">Total</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-[color:var(--brunchi-ink-soft)]">No orders yet.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-[color:var(--brunchi-stone)]" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="px-4 py-3">
                  <div className="font-mono font-semibold">{o.order_number}</div>
                  <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{o.created_at?.slice(0, 16).replace("T", " ")}</div>
                </td>
                <td className="px-4 py-3">{o.customer?.name}<br /><span className="text-xs text-[color:var(--brunchi-ink-soft)]">{o.customer?.email}</span></td>
                <td className="px-4 py-3">{o.shipping_address?.city}<br /><span className="text-xs">{o.shipping_address?.country}</span></td>
                <td className="px-4 py-3 text-xs">{o.items?.map((i) => `${i.quantity}×${i.name}`).join(", ")}</td>
                <td className="px-4 py-3 font-mono">{fmtEUR(o.total_eur)}</td>
                <td className="px-4 py-3">
                  <select value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)}
                    data-testid={`admin-order-status-${o.order_number}`}
                    className="px-2 py-1 rounded border border-[color:var(--brunchi-stone-2)] bg-white text-xs">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => printLabel(o.id, o.order_number)}
                    data-testid={`admin-print-label-${o.order_number}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--brunchi-ink)] text-white text-xs">
                    <Printer size={12} /> Label
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_PROD = { slug: "", name: "", description: "", long_description: "", category: "protein-bars",
  price_eur: 0, weight_g: 60, stock: 0, images: [], ingredients: [], nutrition: {}, tags: [],
  is_active: true, is_featured: false };

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/products", { params: { include_inactive: true } }).then((r) => setItems(r.data));
  useEffect(load, []);

  const save = async () => {
    const p = { ...editing };
    if (typeof p.images === "string") p.images = p.images.split(",").map(s => s.trim()).filter(Boolean);
    if (typeof p.ingredients === "string") p.ingredients = p.ingredients.split(",").map(s => s.trim()).filter(Boolean);
    if (typeof p.tags === "string") p.tags = p.tags.split(",").map(s => s.trim()).filter(Boolean);
    if (typeof p.nutrition === "string") { try { p.nutrition = JSON.parse(p.nutrition); } catch { p.nutrition = {}; } }
    p.price_eur = parseFloat(p.price_eur); p.stock = parseInt(p.stock); p.weight_g = parseInt(p.weight_g || 0) || null;
    try {
      if (p.id) await api.put(`/admin/products/${p.id}`, p);
      else await api.post("/admin/products", p);
      toast.success("Product saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("brunchi_token");
      const res = await fetch(`${API}/admin/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      const data = await res.json();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = data.url.startsWith("http") ? data.url : `${backendUrl}${data.url}`;
      const currentStr = typeof editing.images === "string" ? editing.images : (editing.images || []).join(", ");
      const newImages = currentStr ? `${currentStr}, ${fullUrl}` : fullUrl;
      setEditing({ ...editing, images: newImages });
      toast.success("Image uploaded");
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); load();
  };

  const asString = (v) => Array.isArray(v) ? v.join(", ") : (typeof v === "object" ? JSON.stringify(v) : (v ?? ""));
  const input = "w-full px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm";

  return (
    <div>
      <button onClick={() => setEditing({ ...EMPTY_PROD })}
        data-testid="admin-add-product-btn"
        className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full pill-primary text-sm">
        <Plus size={14} /> New product
      </button>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-4" data-testid={`admin-product-${p.slug}`}>
            <div className="flex items-start gap-3">
              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-16 h-16 object-cover rounded" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif text-base">{p.name}</div>
                <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{p.category} · Stock {p.stock}</div>
                <div className="font-mono text-sm text-[color:var(--brunchi-terracotta)]">{fmtEUR(p.price_eur)}</div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing({ ...p, images: asString(p.images), ingredients: asString(p.ingredients), tags: asString(p.tags), nutrition: asString(p.nutrition) })}
                data-testid={`admin-edit-${p.slug}`}
                className="flex-1 inline-flex justify-center items-center gap-1 px-3 py-1.5 rounded-full border border-[color:var(--brunchi-stone-2)] text-xs">
                <Pencil size={12} /> Edit
              </button>
              <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.slug}`} className="px-3 py-1.5 rounded-full border border-[color:var(--brunchi-error)]/40 text-[color:var(--brunchi-error)] text-xs">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-product-modal">
            <div className="font-serif text-2xl mb-4">{editing.id ? "Edit product" : "New product"}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Slug (unique)" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className={input} data-testid="product-slug" />
              <input placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={input} data-testid="product-name" />
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={input} data-testid="product-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Price (EUR)" value={editing.price_eur} onChange={(e) => setEditing({ ...editing, price_eur: e.target.value })} className={input} data-testid="product-price" />
              <input type="number" placeholder="Stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className={input} data-testid="product-stock" />
              <input type="number" placeholder="Weight (g)" value={editing.weight_g || ""} onChange={(e) => setEditing({ ...editing, weight_g: e.target.value })} className={input} />
              <input placeholder="Short description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={`${input} sm:col-span-2`} />
              <textarea placeholder="Long description" value={editing.long_description} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })} className={`${input} sm:col-span-2`} rows={3} />
              <div className="sm:col-span-2">
                <div className="flex items-center gap-3 mb-2">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[color:var(--brunchi-terracotta)] text-[color:var(--brunchi-terracotta)] text-sm cursor-pointer hover:bg-[color:var(--brunchi-terracotta-light)]" data-testid="product-upload-label">
                    <Upload size={14} /> {uploading ? "Uploading…" : "Upload photo"}
                    <input type="file" accept="image/*" className="hidden" data-testid="product-upload-input"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  </label>
                  <span className="text-xs text-[color:var(--brunchi-ink-soft)]">or paste URLs below (comma separated)</span>
                </div>
                <input placeholder="Image URLs (comma separated)" value={editing.images} onChange={(e) => setEditing({ ...editing, images: e.target.value })} className={input} data-testid="product-images" />
                {typeof editing.images === "string" && editing.images && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {editing.images.split(",").map(s => s.trim()).filter(Boolean).map((url, i) => (
                      <img key={i} src={url} alt="" className="w-14 h-14 rounded object-cover border border-[color:var(--brunchi-stone)]" />
                    ))}
                  </div>
                )}
              </div>
              <input placeholder="Ingredients (comma)" value={editing.ingredients} onChange={(e) => setEditing({ ...editing, ingredients: e.target.value })} className={input} />
              <input placeholder="Tags: vegan, gluten-free" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={input} />
              <input placeholder='Nutrition JSON e.g. {"calories":"180kcal"}' value={editing.nutrition} onChange={(e) => setEditing({ ...editing, nutrition: e.target.value })} className={`${input} sm:col-span-2`} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full pill-outline text-sm">Cancel</button>
              <button onClick={save} data-testid="product-save-btn" className="px-6 py-2 rounded-full pill-primary text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShippingTab() {
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/shipping/rates").then((r) => setRates(r.data));
  useEffect(load, []);
  const empty = { country_code: "", country_name: "", flat_rate_eur: 0, free_over_eur: 0, min_order_eur: 15, estimated_days: "", is_active: true, city_rates: [] };
  const save = async () => {
    const p = { ...editing, flat_rate_eur: parseFloat(editing.flat_rate_eur),
      free_over_eur: parseFloat(editing.free_over_eur || 0), min_order_eur: parseFloat(editing.min_order_eur || 0),
      city_rates: (editing.city_rates || []).filter(c => c.city).map(c => ({
        city: c.city,
        flat_rate_eur: parseFloat(c.flat_rate_eur || 0),
        free_over_eur: c.free_over_eur === "" || c.free_over_eur === null || c.free_over_eur === undefined ? null : parseFloat(c.free_over_eur),
      })) };
    try {
      if (p.id) await api.put(`/admin/shipping/rates/${p.id}`, p);
      else await api.post("/admin/shipping/rates", p);
      toast.success("Rate saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/shipping/rates/${id}`); load(); } };
  const addCity = () => setEditing({ ...editing, city_rates: [...(editing.city_rates || []), { city: "", flat_rate_eur: 0, free_over_eur: "" }] });
  const updCity = (i, patch) => setEditing({ ...editing, city_rates: editing.city_rates.map((c, ix) => ix === i ? { ...c, ...patch } : c) });
  const rmCity = (i) => setEditing({ ...editing, city_rates: editing.city_rates.filter((_, ix) => ix !== i) });
  const input = "w-full px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm";
  return (
    <div>
      <button onClick={() => setEditing({ ...empty })} data-testid="admin-add-rate-btn" className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full pill-primary text-sm">
        <Plus size={14} /> New shipping zone
      </button>
      <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--brunchi-cream-2)]">
            <tr className="text-left">
              <th className="px-4 py-3">Country</th><th className="px-4 py-3">Flat rate</th>
              <th className="px-4 py-3">Free over</th><th className="px-4 py-3">Min order</th>
              <th className="px-4 py-3">Cities</th>
              <th className="px-4 py-3">ETA</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id} className="border-t border-[color:var(--brunchi-stone)]" data-testid={`shipping-row-${r.country_code}`}>
                <td className="px-4 py-3">{r.country_name} <span className="font-mono text-xs text-[color:var(--brunchi-ink-soft)]">({r.country_code})</span></td>
                <td className="px-4 py-3 font-mono">{fmtEUR(r.flat_rate_eur)}</td>
                <td className="px-4 py-3 font-mono">{r.free_over_eur ? fmtEUR(r.free_over_eur) : "—"}</td>
                <td className="px-4 py-3 font-mono">{fmtEUR(r.min_order_eur)}</td>
                <td className="px-4 py-3 text-xs">{(r.city_rates || []).length} zones</td>
                <td className="px-4 py-3 text-xs">{r.estimated_days}</td>
                <td className="px-4 py-3">{r.is_active ? "✓" : "—"}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => setEditing({ ...r, city_rates: r.city_rates || [] })} data-testid={`shipping-edit-${r.country_code}`} className="p-1.5 border rounded"><Pencil size={12} /></button>
                  <button onClick={() => del(r.id)} className="p-1.5 border rounded text-[color:var(--brunchi-error)]"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-serif text-2xl mb-4">{editing.id ? "Edit zone" : "New zone"}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Country code (CY, GR, INTL)" value={editing.country_code} onChange={(e) => setEditing({ ...editing, country_code: e.target.value.toUpperCase() })} className={input} />
              <input placeholder="Country name" value={editing.country_name} onChange={(e) => setEditing({ ...editing, country_name: e.target.value })} className={input} />
              <input type="number" step="0.01" placeholder="Flat rate €" value={editing.flat_rate_eur} onChange={(e) => setEditing({ ...editing, flat_rate_eur: e.target.value })} className={input} />
              <input type="number" step="0.01" placeholder="Free over € (0 = none)" value={editing.free_over_eur} onChange={(e) => setEditing({ ...editing, free_over_eur: e.target.value })} className={input} />
              <input type="number" step="0.01" placeholder="Min order €" value={editing.min_order_eur} onChange={(e) => setEditing({ ...editing, min_order_eur: e.target.value })} className={input} />
              <input placeholder="ETA (e.g. 1-2 days)" value={editing.estimated_days} onChange={(e) => setEditing({ ...editing, estimated_days: e.target.value })} className={input} />
              <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
            </div>

            <div className="mt-6 border-t border-[color:var(--brunchi-stone)] pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-serif text-lg">City rate overrides</div>
                  <div className="text-xs text-[color:var(--brunchi-ink-soft)]">Charge different rates for specific cities. Leave 'Free over' blank to inherit the country default.</div>
                </div>
                <button onClick={addCity} data-testid="add-city-rate-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full pill-outline text-xs">
                  <Plus size={12} /> Add city
                </button>
              </div>
              <div className="space-y-2">
                {(editing.city_rates || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`city-rate-row-${i}`}>
                    <input placeholder="City" value={c.city} onChange={(e) => updCity(i, { city: e.target.value })}
                      className={`${input} col-span-5`} data-testid={`city-name-${i}`} />
                    <input type="number" step="0.01" placeholder="Flat €" value={c.flat_rate_eur}
                      onChange={(e) => updCity(i, { flat_rate_eur: e.target.value })}
                      className={`${input} col-span-3`} data-testid={`city-flat-${i}`} />
                    <input type="number" step="0.01" placeholder="Free over €" value={c.free_over_eur ?? ""}
                      onChange={(e) => updCity(i, { free_over_eur: e.target.value })}
                      className={`${input} col-span-3`} data-testid={`city-free-${i}`} />
                    <button onClick={() => rmCity(i)} className="col-span-1 p-2 text-[color:var(--brunchi-error)]" data-testid={`city-remove-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {(editing.city_rates || []).length === 0 && (
                  <div className="text-xs text-[color:var(--brunchi-ink-soft)] py-3">No city overrides — all cities use the country flat rate.</div>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full pill-outline text-sm">Cancel</button>
              <button onClick={save} data-testid="shipping-save-btn" className="px-6 py-2 rounded-full pill-primary text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiscountsTab() {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { code: "", discount_type: "percent", value: 10, min_order_eur: 0, max_uses: null, expires_at: "", is_active: true };
  const load = () => api.get("/admin/discounts").then((r) => setCodes(r.data));
  useEffect(load, []);
  const save = async () => {
    const p = {
      ...editing, code: editing.code.trim().toUpperCase(),
      value: parseFloat(editing.value),
      min_order_eur: parseFloat(editing.min_order_eur || 0),
      max_uses: editing.max_uses === "" || editing.max_uses === null ? null : parseInt(editing.max_uses),
      expires_at: editing.expires_at || null,
    };
    try {
      if (p.id) await api.put(`/admin/discounts/${p.id}`, p);
      else await api.post("/admin/discounts", p);
      toast.success("Discount saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete this code?")) { await api.delete(`/admin/discounts/${id}`); load(); } };
  const input = "w-full px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm";
  return (
    <div>
      <button onClick={() => setEditing({ ...empty })} data-testid="admin-add-discount-btn"
        className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full pill-primary text-sm">
        <Plus size={14} /> New discount code
      </button>
      <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-discounts-table">
          <thead className="bg-[color:var(--brunchi-cream-2)]">
            <tr className="text-left">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3">Min order</th><th className="px-4 py-3">Used / Max</th>
              <th className="px-4 py-3">Expires</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && <tr><td colSpan={7} className="px-4 py-16 text-center text-[color:var(--brunchi-ink-soft)]">No discount codes yet. Launch offer perhaps?</td></tr>}
            {codes.map((c) => (
              <tr key={c.id} className="border-t border-[color:var(--brunchi-stone)]" data-testid={`discount-row-${c.code}`}>
                <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                <td className="px-4 py-3">{c.discount_type === "percent" ? `${c.value}%` : fmtEUR(c.value)}</td>
                <td className="px-4 py-3 font-mono">{c.min_order_eur ? fmtEUR(c.min_order_eur) : "—"}</td>
                <td className="px-4 py-3 font-mono">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="px-4 py-3 text-xs">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                <td className="px-4 py-3">{c.is_active ? "✓" : "—"}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => setEditing({ ...c, expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "" })}
                    data-testid={`discount-edit-${c.code}`} className="p-1.5 border rounded"><Pencil size={12} /></button>
                  <button onClick={() => del(c.id)} data-testid={`discount-delete-${c.code}`} className="p-1.5 border rounded text-[color:var(--brunchi-error)]"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="discount-modal">
            <div className="font-serif text-2xl mb-4">{editing.id ? "Edit code" : "New discount code"}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="CODE (e.g. LAUNCH10)" value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                className={`${input} sm:col-span-2 font-mono`} data-testid="discount-code" />
              <select value={editing.discount_type} onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })}
                className={input} data-testid="discount-type">
                <option value="percent">Percent off</option>
                <option value="fixed">Fixed € off</option>
              </select>
              <input type="number" step="0.01" placeholder={editing.discount_type === "percent" ? "% off" : "€ off"}
                value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                className={input} data-testid="discount-value" />
              <input type="number" step="0.01" placeholder="Min order € (0 = any)"
                value={editing.min_order_eur} onChange={(e) => setEditing({ ...editing, min_order_eur: e.target.value })}
                className={input} data-testid="discount-min" />
              <input type="number" placeholder="Max uses (empty = unlimited)"
                value={editing.max_uses ?? ""} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })}
                className={input} data-testid="discount-max" />
              <input type="date" placeholder="Expires" value={editing.expires_at || ""}
                onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })}
                className={`${input} sm:col-span-2`} data-testid="discount-expiry" />
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full pill-outline text-sm">Cancel</button>
              <button onClick={save} data-testid="discount-save-btn" className="px-6 py-2 rounded-full pill-primary text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportsTab() {
  const [summary, setSummary] = useState(null);
  const [gran, setGran] = useState("day");
  const [ts, setTs] = useState([]);
  const [byProd, setByProd] = useState([]);
  const [byCity, setByCity] = useState([]);
  const [byPromo, setByPromo] = useState([]);
  const [demo, setDemo] = useState(null);

  const [funnel, setFunnel] = useState(null);
  useEffect(() => { api.get("/admin/reports/summary").then((r) => setSummary(r.data)); api.get("/admin/reports/funnel").then((r) => setFunnel(r.data)); }, []);
  useEffect(() => { api.get("/admin/reports/timeseries", { params: { granularity: gran } }).then((r) => setTs(r.data)); }, [gran]);
  useEffect(() => {
    api.get("/admin/reports/by-product").then((r) => setByProd(r.data));
    api.get("/admin/reports/by-city").then((r) => setByCity(r.data));
    api.get("/admin/reports/by-demographic").then((r) => setDemo(r.data));
    api.get("/admin/reports/by-promo").then((r) => setByPromo(r.data));
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-reports">
      {funnel && (
        <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid="funnel-card">
          <h3 className="font-display text-xl mb-4">Conversion funnel</h3>
          <div className="grid grid-cols-3 gap-4">
            {[
              { k: "Cart adds", v: funnel.cart_adds, test: "funnel-cart" },
              { k: "Checkouts started", v: funnel.checkouts_started, test: "funnel-checkout", rate: funnel.cart_to_checkout_rate },
              { k: "Paid orders", v: funnel.checkouts_paid, test: "funnel-paid", rate: funnel.checkout_to_paid_rate },
            ].map((c) => (
              <div key={c.k} data-testid={c.test}>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-ink-soft)]">{c.k}</div>
                <div className="font-display text-2xl font-semibold mt-1">{c.v}</div>
                {c.rate !== undefined && <div className="text-[10px] font-mono text-[color:var(--brunchi-green-hover)] mt-0.5">{Math.round(c.rate * 100)}% from previous step</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { k: "Revenue", v: fmtEUR(summary?.revenue_eur || 0), test: "kpi-revenue" },
          { k: "Orders", v: summary?.orders ?? 0, test: "kpi-orders" },
          { k: "Avg order", v: fmtEUR(summary?.avg_order_eur || 0), test: "kpi-avg" },
          { k: "Awaiting prep", v: summary?.pending_prep ?? 0, test: "kpi-pending" },
        ].map((c) => (
          <div key={c.k} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid={c.test}>
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-forest)]">{c.k}</div>
            <div className="mt-2 font-serif text-2xl lg:text-3xl font-semibold">{c.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl">Sales over time</h3>
          <select value={gran} onChange={(e) => setGran(e.target.value)} data-testid="report-granularity"
            className="px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm">
            <option value="day">Daily</option><option value="week">Weekly</option>
            <option value="month">Monthly</option><option value="year">Yearly</option>
          </select>
        </div>
        <div style={{ height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={ts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5DEC9" />
              <XAxis dataKey="key" stroke="#3D3A34" fontSize={11} />
              <YAxis stroke="#3D3A34" fontSize={11} />
              <Tooltip formatter={(v, n) => n === "revenue_eur" ? fmtEUR(v) : v} />
              <Line type="monotone" dataKey="revenue_eur" name="Revenue" stroke="#8C3B1D" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5">
          <h3 className="font-serif text-xl mb-4">Top products</h3>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={byProd.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DEC9" />
                <XAxis dataKey="name" stroke="#3D3A34" fontSize={10} angle={-15} height={60} />
                <YAxis stroke="#3D3A34" fontSize={11} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Bar dataKey="revenue_eur" fill="#2D4030" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5">
          <h3 className="font-serif text-xl mb-4">By city</h3>
          <div className="max-h-64 overflow-y-auto text-sm">
            <table className="w-full">
              <thead><tr className="text-left text-xs uppercase text-[color:var(--brunchi-forest)]"><th className="py-2">City</th><th>Country</th><th>Orders</th><th className="text-right">Revenue</th></tr></thead>
              <tbody>
                {byCity.map((c, i) => (
                  <tr key={i} className="border-t border-[color:var(--brunchi-stone)]">
                    <td className="py-2">{c.city || "—"}</td><td>{c.country}</td><td>{c.orders}</td><td className="text-right font-mono">{fmtEUR(c.revenue_eur)}</td>
                  </tr>
                ))}
                {byCity.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-[color:var(--brunchi-ink-soft)]">No data yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid="promo-report-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Promo codes</h3>
            <div className="text-xs text-[color:var(--brunchi-ink-soft)]">by revenue driven</div>
          </div>
          <div className="max-h-64 overflow-y-auto text-sm">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase text-[color:var(--brunchi-forest)]">
                  <th className="py-2">Code</th>
                  <th>Source</th>
                  <th>Orders</th>
                  <th className="text-right">Discount</th>
                  <th className="text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {byPromo.map((p) => (
                  <tr key={p.code} className="border-t border-[color:var(--brunchi-stone)]" data-testid={`promo-row-${p.code}`}>
                    <td className="py-2">
                      <div className="font-mono text-xs font-semibold">{p.code}</div>
                      {p.discount_type && (
                        <div className="text-[10px] text-[color:var(--brunchi-ink-soft)]">
                          {p.discount_type === "percent" ? `${p.discount_value}% off` : `€${Number(p.discount_value).toFixed(0)} off`}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        p.source === "loyalty" ? "bg-[color:var(--brunchi-green-light)] text-[color:var(--brunchi-green-hover)]"
                        : p.source === "referral" ? "bg-amber-50 text-[color:var(--brunchi-amber)]"
                        : p.source === "bundle" ? "bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-forest)]"
                        : "bg-[color:var(--brunchi-cream)] text-[color:var(--brunchi-ink-soft)]"
                      }`}>{p.source}</span>
                    </td>
                    <td className="font-mono">{p.orders}</td>
                    <td className="text-right font-mono text-[color:var(--brunchi-terracotta)]">−{fmtEUR(p.discount_eur)}</td>
                    <td className="text-right font-mono font-semibold">{fmtEUR(p.revenue_eur)}</td>
                  </tr>
                ))}
                {byPromo.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-[color:var(--brunchi-ink-soft)]" data-testid="promo-report-empty">
                      No promo redemptions yet — codes will appear here after first paid order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5">
          <h3 className="font-serif text-xl mb-4">By sex</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={demo?.by_sex || []} dataKey="orders" nameKey="key" cx="50%" cy="50%" outerRadius={80} label>
                  {(demo?.by_sex || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5">
          <h3 className="font-serif text-xl mb-4">By age group</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={demo?.by_age || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DEC9" />
                <XAxis dataKey="key" stroke="#3D3A34" fontSize={11} />
                <YAxis stroke="#3D3A34" fontSize={11} />
                <Tooltip formatter={(v) => fmtEUR(v)} />
                <Bar dataKey="revenue_eur" fill="#D9822B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
